import React, { useState, useEffect } from 'react';
import { Card, Button, message } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import QuizStatistics from '@/components/quizzes/QuizStatistics';
import QuestionsTable from '@/components/quizzes/QuestionsTable';
import QuestionsFilters from '@/components/quizzes/QuestionsFilters';
import QuestionFormModal from '@/components/quizzes/QuestionFormModal';
import { miniQuizService } from '@/services/miniQuizService';
import type {
  MiniQuizQuestion,
  MiniQuizStatistics,
  GetQuestionsParams,
} from '@/types';

const MiniQuizPage: React.FC = () => {
  const [questions, setQuestions] = useState<MiniQuizQuestion[]>([]);
  const [statistics, setStatistics] = useState<MiniQuizStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<MiniQuizQuestion | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [filters, setFilters] = useState<GetQuestionsParams>({
    page: 1,
    limit: 10,
  });

  // Load questions
  const loadQuestions = async (params?: GetQuestionsParams) => {
    try {
      setLoading(true);
      const queryParams = { ...filters, ...params };
      const response = await miniQuizService.getQuestions(queryParams);
      
      setQuestions(response.questions);
      setPagination({
        page: queryParams.page || 1,
        limit: queryParams.limit || 10,
        total: response.total,
      });
    } catch (error) {
      message.error('Failed to load questions');
      console.error('Error loading questions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load statistics
  const loadStatistics = async () => {
    try {
      setStatsLoading(true);
      const stats = await miniQuizService.getStatistics();
      setStatistics(stats);
    } catch (error) {
      message.error('Failed to load statistics');
      console.error('Error loading statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: GetQuestionsParams) => {
    const updatedFilters = { ...newFilters, page: 1 };
    setFilters(updatedFilters);
    loadQuestions(updatedFilters);
  };

  // Handle pagination changes
  const handleTableChange = (page: number, pageSize: number) => {
    const updatedFilters = { ...filters, page, limit: pageSize };
    setFilters(updatedFilters);
    loadQuestions(updatedFilters);
  };

  // Handle create question
  const handleCreateQuestion = () => {
    setEditingQuestion(null);
    setModalVisible(true);
  };

  // Handle edit question
  const handleEditQuestion = (question: MiniQuizQuestion) => {
    setEditingQuestion(question);
    setModalVisible(true);
  };

  // Handle delete question
  const handleDeleteQuestion = async (id: number) => {
    try {
      await miniQuizService.deleteQuestion(id);
      message.success('Question deleted successfully');
      loadQuestions();
      loadStatistics();
    } catch (error) {
      message.error('Failed to delete question');
      console.error('Error deleting question:', error);
    }
  };

  // Handle bulk status update
  const handleBulkStatusUpdate = async (
    questionIds: number[],
    isActive: boolean
  ) => {
    try {
      await miniQuizService.bulkUpdateStatus(questionIds, isActive);
      message.success(
        `Questions ${isActive ? 'activated' : 'deactivated'} successfully`
      );
      loadQuestions();
      loadStatistics();
    } catch (error) {
      message.error('Failed to update question status');
      console.error('Error updating status:', error);
    }
  };

  // Handle form submission
  const handleFormSubmit = async (values: any) => {
    try {
      if (editingQuestion) {
        await miniQuizService.updateQuestion(editingQuestion.id, values);
        message.success('Question updated successfully');
      } else {
        await miniQuizService.createQuestion(values);
        message.success('Question created successfully');
      }
      
      setModalVisible(false);
      setEditingQuestion(null);
      loadQuestions();
      loadStatistics();
    } catch (error) {
      message.error(
        editingQuestion 
          ? 'Failed to update question' 
          : 'Failed to create question'
      );
      console.error('Error saving question:', error);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    loadQuestions();
    loadStatistics();
  };

  // Initial load
  useEffect(() => {
    loadQuestions();
    loadStatistics();
  }, []);

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>
            MiniQuiz Management
          </h1>
          <p style={{ margin: '4px 0 0 0', color: '#666' }}>
            Manage quiz questions and monitor statistics
          </p>
        </div>
        <div>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            style={{ marginRight: 8 }}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateQuestion}
          >
            Add Question
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <QuizStatistics
        statistics={statistics}
        loading={statsLoading}
      />

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <QuestionsFilters
          filters={filters}
          onFiltersChange={handleFilterChange}
          onClear={() => {
            const resetFilters = { page: 1, limit: 10 };
            setFilters(resetFilters);
            loadQuestions(resetFilters);
          }}
        />
      </Card>

      {/* Questions Table */}
      <Card>
        <QuestionsTable
          questions={questions}
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number, range: [number, number]) =>
              `${range[0]}-${range[1]} of ${total} questions`,
          }}
          onEdit={handleEditQuestion}
          onDelete={handleDeleteQuestion}
          onBulkStatusUpdate={handleBulkStatusUpdate}
          onTableChange={handleTableChange}
        />
      </Card>

      {/* Form Modal */}
      <QuestionFormModal
        visible={modalVisible}
        editingQuestion={editingQuestion}
        onSubmit={handleFormSubmit}
        onCancel={() => {
          setModalVisible(false);
          setEditingQuestion(null);
        }}
      />
    </div>
  );
};

export default MiniQuizPage;
